import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Trash2, Plus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { ABSENCE_TYPES, fmtDate } from "@/lib/format";
import { AbsenceForm, AbsenceStatus } from "@/components/AbsenceForm";
import { Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const RejectDialog = ({ absence, onClose, onDone }) => {
  const [note, setNote] = useState("");
  const send = async () => {
    try {
      await api.post(`/hr/absences/${absence.id}/decision`, { decision: "reject", note });
      toast.success("Richiesta respinta");
      onDone();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!absence} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="absence-reject-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Respingi richiesta</DialogTitle></DialogHeader>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Motivo del rifiuto (visibile al tecnico)" data-testid="absence-reject-note" />
        <DialogFooter><Button onClick={send} disabled={!note.trim()} data-testid="absence-reject-confirm">Respingi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ a, canApprove, canPlan, onApprove, onReject, onDelete }) => (
  <TableRow data-testid={`absence-row-${a.id}`}>
    <TableCell className="text-sm font-semibold">{a.employee_name}</TableCell>
    <TableCell className="text-sm">{ABSENCE_TYPES[a.type]}</TableCell>
    <TableCell className="font-mono text-xs">{fmtDate(a.date_from)}{a.date_to !== a.date_from && ` → ${fmtDate(a.date_to)}`}</TableCell>
    <TableCell><AbsenceStatus status={a.status} testId={`absence-status-${a.id}`} /></TableCell>
    <TableCell className="text-xs text-muted-foreground">{a.requested_by}{a.notes && <div className="text-foreground">{a.notes}</div>}</TableCell>
    <TableCell className="text-right">
      {a.status === "REQUESTED" && canApprove && <>
        <Button size="sm" variant="outline" className="mr-1" onClick={() => onApprove(a)} data-testid={`absence-approve-${a.id}`}><Check className="mr-1 h-3.5 w-3.5" />Approva</Button>
        <Button size="sm" variant="outline" className="mr-1" onClick={() => onReject(a)} data-testid={`absence-reject-${a.id}`}><X className="mr-1 h-3.5 w-3.5" />Respingi</Button>
      </>}
      {canPlan && <Button size="icon" variant="ghost" onClick={() => onDelete(a)} aria-label="Elimina" data-testid={`absence-delete-${a.id}`}><Trash2 className="h-4 w-4" /></Button>}
    </TableCell>
  </TableRow>
);

export const AbsencesPanel = () => {
  const { can } = useAuth();
  const { employees: allEmployees, maps, companyId } = useData();
  const employees = companyId ? allEmployees.filter((e) => e.company_id === companyId) : allEmployees;
  const [status, setStatus] = useState("all");
  const [rows, setList] = useState([]);
  const list = companyId ? rows.filter((a) => maps.employees?.[a.employee_id]?.company_id === companyId) : rows;
  const [creating, setCreating] = useState(false);
  const [rejecting, setRejecting] = useState(null);
  const load = useCallback(() => api.get("/hr/absences", { params: { status: status === "all" ? undefined : status } }).then((r) => setList(r.data)).catch(() => {}), [status]);
  useEffect(() => { load(); }, [load]);
  const act = (fn, msg) => async (a) => {
    try {
      await fn(a);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const approve = act((a) => api.post(`/hr/absences/${a.id}/decision`, { decision: "approve" }), "Richiesta approvata");
  const remove = act((a) => api.delete(`/hr/absences/${a.id}`), "Assenza eliminata");
  const pending = list.filter((a) => a.status === "REQUESTED").length;

  return (
    <div data-testid="absences-panel">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48 bg-white" data-testid="absences-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti gli stati</SelectItem><SelectItem value="REQUESTED">In attesa</SelectItem><SelectItem value="APPROVED">Approvate</SelectItem><SelectItem value="REJECTED">Respinte</SelectItem></SelectContent>
        </Select>
        {pending > 0 && <span className="bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-900" data-testid="absences-pending-count">{pending} richieste da approvare</span>}
        {can("hr:plan") && <Button className="ml-auto" onClick={() => setCreating(true)} data-testid="absences-new-button"><Plus className="mr-1 h-4 w-4" />Nuova assenza</Button>}
      </div>
      {list.length === 0 ? <Empty text="Nessuna assenza" testId="absences-empty" /> : (
        <div className="overflow-x-auto border bg-white">
          <Table>
            <TableHeader><TableRow className="bg-muted/50"><TableHead>Dipendente</TableHead><TableHead>Tipo</TableHead><TableHead>Periodo</TableHead><TableHead>Stato</TableHead><TableHead>Inserita da / note</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>{list.map((a) => <Row key={a.id} a={a} canApprove={can("hr:approve")} canPlan={can("hr:plan")} onApprove={approve} onReject={setRejecting} onDelete={remove} />)}</TableBody>
          </Table>
        </div>
      )}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid="absence-create-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Nuova assenza</DialogTitle></DialogHeader>
          <AbsenceForm employees={employees} onSaved={() => { setCreating(false); load(); }} testPrefix="absence" />
        </DialogContent>
      </Dialog>
      <RejectDialog absence={rejecting} onClose={() => setRejecting(null)} onDone={load} />
    </div>
  );
};
